import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { setTrainingRouteLocale } from '../../i18n';
import TrainingCataloguePage from './pages/TrainingCataloguePage';
import TrainingNotFoundPage, { getTrainingNotFoundContent } from './pages/TrainingNotFoundPage';
import TrainingOfferPage from './pages/TrainingOfferPage';
import { getCatalogueContent, getTrainingContent } from './trainingContent';
import { getAlternatePaths, getCataloguePath, resolveTrainingRoute } from './trainingRoutes';

const SITE_ORIGIN = 'https://csl-brands.com';
const TRAINING_HEAD_ATTRIBUTE = 'data-training-route-head';
const TRAINING_ALTERNATE_LANGUAGES = ['fr', 'en', 'x-default'];

const setAttributeIfChanged = (element, name, value) => {
  if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
};

const getAbsoluteUrl = (pathname) => `${SITE_ORIGIN}${pathname}`;
const isTrainingPathname = (pathname) => ['fr', 'en'].some((locale) => {
  const cataloguePath = getCataloguePath(locale);
  return pathname === cataloguePath || pathname.startsWith(`${cataloguePath}/`);
});

const synchronizeTrainingHead = (headState, pathname, locale) => {
  headState.active = true;
  if (headState.releaseFrame !== undefined) {
    window.cancelAnimationFrame(headState.releaseFrame);
    headState.releaseFrame = undefined;
  }
  const canonicalCandidates = [...document.head.querySelectorAll('link[rel="canonical"]')];
  let canonical = headState.canonical;

  if (!canonical?.isConnected) {
    canonical = canonicalCandidates[0] || document.createElement('link');
    headState.canonical = canonical;
    headState.canonicalCreated = canonicalCandidates.length === 0;
    headState.canonicalOriginalHref = canonical.getAttribute('href');
    headState.canonicalOriginalMarker = canonical.getAttribute(TRAINING_HEAD_ATTRIBUTE);
    if (!canonical.isConnected) document.head.appendChild(canonical);
  }

  setAttributeIfChanged(canonical, 'rel', 'canonical');
  setAttributeIfChanged(canonical, 'href', getAbsoluteUrl(pathname));
  setAttributeIfChanged(canonical, TRAINING_HEAD_ATTRIBUTE, 'canonical');
  [...document.head.querySelectorAll('link[rel="canonical"]')]
    .filter((link) => link !== canonical)
    .forEach((link) => link.remove());

  const alternatePaths = getAlternatePaths(pathname)
    || getAlternatePaths(getCataloguePath(locale));
  const alternateValues = {
    fr: alternatePaths.fr,
    en: alternatePaths.en,
    'x-default': alternatePaths.xDefault,
  };

  TRAINING_ALTERNATE_LANGUAGES.forEach((hrefLang) => {
    let alternate = headState.alternates[hrefLang];
    if (!alternate?.isConnected) {
      alternate = document.createElement('link');
      headState.alternates[hrefLang] = alternate;
      document.head.appendChild(alternate);
    }

    setAttributeIfChanged(alternate, 'rel', 'alternate');
    setAttributeIfChanged(alternate, 'hreflang', hrefLang);
    setAttributeIfChanged(alternate, 'href', getAbsoluteUrl(alternateValues[hrefLang]));
    setAttributeIfChanged(alternate, TRAINING_HEAD_ATTRIBUTE, hrefLang);
    [...document.head.querySelectorAll(`link[rel="alternate"][hreflang="${hrefLang}"]`)]
      .filter((link) => link !== alternate)
      .forEach((link) => link.remove());
  });
};

const restoreTrainingHead = (headState) => {
  headState.active = false;
  const canonical = headState.canonical;
  if (!canonical) return;

  const connectedSuccessor = [...document.head.querySelectorAll('link[rel="canonical"]')]
    .find((link) => link !== canonical);
  if (headState.successorHead && !connectedSuccessor) {
    setAttributeIfChanged(canonical, 'rel', 'canonical');
    setAttributeIfChanged(canonical, 'href', headState.successorHead.canonical);
    canonical.removeAttribute(TRAINING_HEAD_ATTRIBUTE);
    TRAINING_ALTERNATE_LANGUAGES.forEach((hrefLang) => {
      const alternate = headState.alternates[hrefLang];
      const successorHref = headState.successorHead.alternates[hrefLang];
      if (!alternate?.isConnected || !successorHref) return;
      setAttributeIfChanged(alternate, 'rel', 'alternate');
      setAttributeIfChanged(alternate, 'hreflang', hrefLang);
      setAttributeIfChanged(alternate, 'href', successorHref);
      alternate.removeAttribute(TRAINING_HEAD_ATTRIBUTE);
    });
    headState.alternates = {};
    headState.canonical = undefined;
    headState.successorHead = undefined;
    return;
  }

  Object.values(headState.alternates).forEach((alternate) => alternate.remove());
  headState.alternates = {};

  const releaseToSuccessor = () => {
    const successorCanonical = [...document.head.querySelectorAll('link[rel="canonical"]')]
      .find((link) => link !== canonical);
    if (!successorCanonical) return false;

    canonical.remove();
    return true;
  };

  if (!releaseToSuccessor() && headState.canonicalCreated) {
    canonical.remove();
  } else if (canonical.isConnected) {
    if (headState.canonicalOriginalHref === null) canonical.removeAttribute('href');
    else canonical.setAttribute('href', headState.canonicalOriginalHref);
    if (headState.canonicalOriginalMarker === null) {
      canonical.removeAttribute(TRAINING_HEAD_ATTRIBUTE);
    } else {
      canonical.setAttribute(TRAINING_HEAD_ATTRIBUTE, headState.canonicalOriginalMarker);
    }
  }

  headState.releaseFrame = window.requestAnimationFrame(() => {
    headState.releaseFrame = undefined;
    if (!headState.active) releaseToSuccessor();
  });
  headState.canonical = undefined;
};

const synchronizeTrainingLocale = async (locale) => {
  try {
    await setTrainingRouteLocale(locale);
  } catch {
    try {
      await setTrainingRouteLocale(locale);
    } catch {
      // Route copy is locale-explicit; the path-authoritative document lang was set before both attempts.
    }
  }
};

const restoreTabIndex = (element, previousTabIndex) => {
  if (previousTabIndex === null) {
    element.removeAttribute('tabindex');
  } else {
    element.setAttribute('tabindex', previousTabIndex);
  }
};

const getTrainingRouteTitle = (route) => {
  if (route.type === 'catalogue') {
    return getCatalogueContent(route.locale)?.seo.title;
  }

  if (route.type === 'offer') {
    return getTrainingContent(route.locale, route.offerKey)?.seo.title;
  }

  return getTrainingNotFoundContent(route.locale)?.seoTitle;
};

export default function TrainingRoute() {
  const location = useLocation();
  const route = resolveTrainingRoute(location.pathname);
  const [readyPath, setReadyPath] = useState();
  const localeQueueRef = useRef(Promise.resolve());
  const previousReadyPathRef = useRef();
  const titleBeforeTransitionRef = useRef(document.title);
  const headStateRef = useRef({ alternates: {} });

  useEffect(() => {
    let current = true;
    titleBeforeTransitionRef.current = document.title;
    setReadyPath(undefined);

    const synchronization = localeQueueRef.current
      .catch(() => undefined)
      .then(() => synchronizeTrainingLocale(route.locale));
    localeQueueRef.current = synchronization;

    synchronization.then(() => {
      if (current) {
        setReadyPath(location.pathname);
      }
    });

    return () => {
      current = false;
    };
  }, [location.pathname, route.locale]);

  useEffect(() => () => restoreTrainingHead(headStateRef.current), []);

  useLayoutEffect(() => {
    if (readyPath !== location.pathname) return undefined;

    const documentElement = document.documentElement;
    const preserveRouteLocale = () => {
      if (documentElement.lang !== route.locale) {
        documentElement.lang = route.locale;
      }
    };
    const observer = new MutationObserver(preserveRouteLocale);
    let headObserver;
    const preserveTrainingHeadWhileCurrent = () => {
      if (!isTrainingPathname(window.location.pathname)
        || window.location.pathname !== location.pathname) {
        const successorCanonical = [...document.head.querySelectorAll('link[rel="canonical"]')]
          .find((link) => link !== headStateRef.current.canonical);
        if (successorCanonical) {
          headStateRef.current.successorHead = {
            canonical: successorCanonical.href,
            alternates: Object.fromEntries(TRAINING_ALTERNATE_LANGUAGES.map((hrefLang) => {
              const successorAlternate = [...document.head.querySelectorAll(
                `link[rel="alternate"][hreflang="${hrefLang}"]`,
              )].find((link) => link !== headStateRef.current.alternates[hrefLang]);
              return [hrefLang, successorAlternate?.href];
            })),
          };
        }
        headObserver.disconnect();
        return;
      }
      synchronizeTrainingHead(headStateRef.current, location.pathname, route.locale);
    };
    headObserver = new MutationObserver(preserveTrainingHeadWhileCurrent);

    document.title = getTrainingRouteTitle(route);
    preserveRouteLocale();
    synchronizeTrainingHead(headStateRef.current, location.pathname, route.locale);
    observer.observe(documentElement, { attributes: true, attributeFilter: ['lang'] });
    headObserver.observe(document.head, {
      attributes: true,
      attributeFilter: ['href', 'hreflang', 'rel'],
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      headObserver.disconnect();
    };
  }, [location.pathname, readyPath, route]);

  useEffect(() => {
    if (readyPath !== location.pathname) return undefined;

    if (previousReadyPathRef.current === undefined) {
      previousReadyPathRef.current = readyPath;
      return undefined;
    }

    previousReadyPathRef.current = readyPath;
    let focusTarget;
    let previousTabIndex;
    let removeTemporaryTabIndex;
    let titleObserver;
    let focusTimer;
    const focusPage = () => {
      titleObserver?.disconnect();
      window.clearTimeout(focusTimer);
      focusTarget = document.querySelector('.training-page h1')
        || document.querySelector('.training-page main');
      if (!focusTarget) return;

      previousTabIndex = focusTarget.getAttribute('tabindex');
      focusTarget.setAttribute('tabindex', '-1');
      removeTemporaryTabIndex = () => restoreTabIndex(focusTarget, previousTabIndex);
      focusTarget.addEventListener('blur', removeTemporaryTabIndex, { once: true });
      focusTarget.focus();
    };
    const focusWhenTitleIsReady = () => {
      if (document.title !== titleBeforeTransitionRef.current) {
        focusPage();
      }
    };

    titleObserver = new MutationObserver(focusWhenTitleIsReady);
    titleObserver.observe(document.head, { childList: true, characterData: true, subtree: true });
    focusWhenTitleIsReady();
    focusTimer = window.setTimeout(focusPage, 1000);

    return () => {
      titleObserver.disconnect();
      window.clearTimeout(focusTimer);
      if (focusTarget && removeTemporaryTabIndex) {
        focusTarget.removeEventListener('blur', removeTemporaryTabIndex);
        restoreTabIndex(focusTarget, previousTabIndex);
      }
    };
  }, [location.pathname, readyPath, route.type]);

  if (readyPath !== location.pathname) {
    return null;
  }

  if (route.type === 'catalogue') {
    return <TrainingCataloguePage locale={route.locale} />;
  }

  if (route.type === 'offer') {
    return <TrainingOfferPage locale={route.locale} offerKey={route.offerKey} />;
  }

  return <TrainingNotFoundPage locale={route.locale} />;
}
